import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, IconButton, Paper, TextField, MenuItem, InputAdornment, Typography, Tooltip,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import * as C from '../../theme/colors.constants';
import { normalizeCnpj, formatCnpjAlphanumeric } from '../../../helpers/utils/cnpj.utils';

/**
 * Primitivos de UI do protótipo.
 *
 * Cada um espelha um componente que já existe no abe-web-react
 * (`src/components/UI/...`), mantendo a mesma intenção de API para que a
 * substituição futura seja direta. As diferenças estão comentadas.
 */

/** Equivale a `UI/Paper/PaperUI` + `UI/CardWrapper`. */
export const CardWrapper = styled(Paper)(({ theme }) => ({
  padding: 'clamp(16px, 1.15vw, 30px)',
  borderRadius: C.radiusCard,
  boxShadow: C.shadowCard,
  backgroundColor: C.white,
  width: '100%',
  // minWidth: 0 permite o card encolher dentro de um flex/grid; sem isso o
  // conteúdo (tabelas largas) define o piso e o layout gera scroll lateral.
  minWidth: 0,
  overflow: 'hidden',
  [theme.breakpoints.down('lg')]: { padding: 16 },
}));

/** Equivale a `UI/Button/ButtonUI` (variante `primary`, pill navy preenchido). */
export function ButtonUI({ children, variant = 'contained', ...rest }) {
  return (
    <Button variant={variant} color="primary" {...rest}>
      {children}
    </Button>
  );
}
ButtonUI.propTypes = { children: PropTypes.node, variant: PropTypes.string };
ButtonUI.defaultProps = { children: null, variant: 'contained' };

/** Equivale a `ButtonOutlined/ButtonOurlined` — pill de contorno navy. */
export function ButtonOutlined({ children, ...rest }) {
  return (
    <Button variant="outlined" color="primary" {...rest}>
      {children}
    </Button>
  );
}
ButtonOutlined.propTypes = { children: PropTypes.node };
ButtonOutlined.defaultProps = { children: null };

/** Label acima do campo, como nos prints (não é o label flutuante do MUI). */
export function FieldLabel({ children, sx }) {
  return (
    <Typography
      component="label"
      sx={{
        display: 'block',
        fontSize: 'clamp(11px, 0.62vw, 14px)',
        fontWeight: 700,
        color: C.black,
        mb: 0.5,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}
FieldLabel.propTypes = { children: PropTypes.node, sx: PropTypes.object };
FieldLabel.defaultProps = { children: null, sx: {} };

/**
 * Campo de texto/select/data com label externo.
 * Cobre `UI/TextField/TextFieldUI`, `UI/Select/SelectUI` e `UI/DateTextInput`
 * numa única assinatura, porque no protótipo não há validação real.
 */
export function Field({
  label, tipo = 'texto', options = [], value = '', onChange, placeholder, fullWidth = true, sx, ...rest
}) {
  const isSelect = tipo === 'select';
  const isDate = tipo === 'data';
  const isDocumento = tipo === 'documento';

  const handleDocumentoChange = (e) => {
    const raw = normalizeCnpj(e.target.value).slice(0, 14);
    onChange({ target: { value: raw } });
  };

  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined, ...sx }}>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <TextField
        select={isSelect}
        type={isDate ? 'date' : 'text'}
        size="small"
        fullWidth={fullWidth}
        value={isDocumento ? formatCnpjAlphanumeric(value || '') : value}
        onChange={isDocumento ? handleDocumentoChange : onChange}
        placeholder={placeholder}
        SelectProps={{ displayEmpty: true }}
        inputProps={{
          ...(isDate ? { 'aria-label': label } : null),
          ...(isDocumento ? { maxLength: 18 } : null),
        }}
        {...rest}
      >
        {isSelect
          ? [
            <MenuItem key="__blank" value="">
              <em style={{ color: C.greyish }}>Selecione</em>
            </MenuItem>,
            ...options.map((o) => {
              const isObjectOption = typeof o === 'object' && o !== null;
              const optionValue = isObjectOption ? o.id : o;
              const optionLabel = isObjectOption ? o.value : o;
              return (
                <MenuItem key={optionValue} value={optionValue}>
                  {optionLabel}
                </MenuItem>
              );
            }),
          ]
          : null}
      </TextField>
    </Box>
  );
}
Field.propTypes = {
  label: PropTypes.string,
  tipo: PropTypes.oneOf(['texto', 'select', 'data', 'documento']),
  options: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.string,
    }),
  ])),
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};
Field.defaultProps = {
  label: null,
  tipo: 'texto',
  options: [],
  value: '',
  onChange: () => null,
  placeholder: '',
  fullWidth: true,
  sx: {},
};

/** Par de datas "de / e / até", como em "Abertos entre:". */
export function PeriodoField({
  label, valor = {}, onChange, separador = 'e', disabled = false,
}) {
  const set = k => e => onChange && onChange({ ...valor, [k]: e.target.value });
  return (
    <Box sx={{ width: '100%' }}>
      {label ? <FieldLabel>{label}</FieldLabel> : null}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField type="date" size="small" fullWidth disabled={disabled} value={valor.inicio || ''} onChange={set('inicio')} />
        <Typography variant="body2" sx={{ color: C.warmGrey, flexShrink: 0 }}>
          {separador}
        </Typography>
        <TextField type="date" size="small" fullWidth disabled={disabled} value={valor.fim || ''} onChange={set('fim')} />
      </Box>
    </Box>
  );
}
PeriodoField.propTypes = {
  label: PropTypes.string,
  valor: PropTypes.object,
  onChange: PropTypes.func,
  separador: PropTypes.string,
  disabled: PropTypes.bool,
};
PeriodoField.defaultProps = {
  label: null,
  valor: {},
  onChange: () => null,
  separador: 'e',
  disabled: false,
};

/** Equivale a `SearchButton/SeachButton`, agora em navy no lugar do rosa. */
export function SearchIconButton({ sx, ...rest }) {
  return (
    <IconButton
      sx={{
        color: C.navy,
        '&:hover': { backgroundColor: 'rgba(13,43,82,0.08)' },
        ...sx,
      }}
      {...rest}
    >
      <SearchIcon />
    </IconButton>
  );
}
SearchIconButton.propTypes = { sx: PropTypes.object };
SearchIconButton.defaultProps = { sx: {} };

/** Campo de busca com lupa embutida (usado na home e no topo da grid). */
export function SearchField({
  placeholder, value, onChange, onSubmit, disabled, loading, sx,
}) {
  return (
    <TextField
      size="small"
      fullWidth
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      onKeyDown={e => e.key === 'Enter' && onSubmit && onSubmit()}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {loading
              ? <CircularProgress size={18} />
              : <SearchIconButton size="small" onClick={onSubmit} disabled={disabled} />}
          </InputAdornment>
        ),
      }}
      sx={sx}
    />
  );
}
SearchField.propTypes = {
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  sx: PropTypes.object,
};
SearchField.defaultProps = {
  placeholder: '',
  value: '',
  onChange: () => null,
  onSubmit: () => null,
  disabled: false,
  loading: false,
  sx: {},
};

/** Equivale a `UI/Icon/WhiteCircleIcon` — o "chip" branco dos ícones de alerta. */
export const WhiteCircleIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: C.white,
  boxShadow: C.shadowChip,
  flexShrink: 0,
  lineHeight: 0,
});

/** Título de seção dentro de um card. Equivale a `Title/Title`. */
export function SectionTitle({
  children, hint, right, sx,
}) {
  const node = (
    <Typography variant="h3" sx={{ color: C.navy, ...sx }}>
      {children}
    </Typography>
  );
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5,
    }}
    >
      {hint ? <Tooltip title={hint} arrow>{node}</Tooltip> : node}
      {right}
    </Box>
  );
}
SectionTitle.propTypes = {
  children: PropTypes.node,
  hint: PropTypes.string,
  right: PropTypes.node,
  sx: PropTypes.object,
};
SectionTitle.defaultProps = {
  children: null,
  hint: null,
  right: null,
  sx: {},
};

/** Formatação monetária pt-BR usada em toda a grid. */
export const formatBRL = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
